import { DeparturePriceBucket } from "@b2bportal/air-shopping-api";
import { HopperThemingProvider } from "@hopper-b2b/hopper-theming";
import { useI18nContext } from "@hopper-b2b/i18n";
import { IMonthBucket, TripCategory } from "@hopper-b2b/types";
import { DatePickerButton } from "@hopper-b2b/ui";
import { Calendar, PickerType } from "@hopper-b2b/ui-core";
import { useDeviceTypes } from "@hopper-b2b/utilities";
import { Deal } from "@hopper-proto/hopper/dealfeed/proto/v1/Deal";
import { useCallback, useMemo, useState } from "react";
import {
  CalendarPicker,
  CalendarTrackingProperties,
} from "./CalendarPicker/CalendarPicker";
import styles from "./CalendarPickerButton.module.scss";

export interface ICalenderPickerButtonProps {
  returnDate: Date | null;
  departureDate: Date | null;
  classes?: string[];
  onComplete?: () => void;
  disabled?: boolean;
  tripCategory: TripCategory;
  setDepartureDate: (value: Date | null) => void;
  setReturnDate: (value: Date | null) => void;
  trackingProperties?: CalendarTrackingProperties;
  startLabel?: string;
  endLabel?: string;
  months?: IMonthBucket[];
  prices?: string[];
  headerTitle: string;
  onCalendarClick?: () => void;
  paperClassName?: string;
  dateFormat?: string;
  preventSameDateSelection?: boolean;
  useNewCalendar?: boolean;
  departureDateBucketsForNewCalendar?: DeparturePriceBucket[];
  forceDisplayBucketsAndDeals?: boolean;
  dealsNewCalendar?: Deal[];
}

export const CalenderPickerButton = ({
  classes = [],
  returnDate,
  departureDate,
  tripCategory,
  disabled,
  onComplete,
  setDepartureDate,
  setReturnDate,
  trackingProperties,
  startLabel,
  endLabel,
  months,
  prices,
  headerTitle,
  onCalendarClick,
  paperClassName,
  dateFormat,
  preventSameDateSelection,
  useNewCalendar = false,
  departureDateBucketsForNewCalendar,
  forceDisplayBucketsAndDeals = false,
  dealsNewCalendar = [],
}: ICalenderPickerButtonProps) => {
  const { t } = useI18nContext();

  const { matchesMobile } = useDeviceTypes();

  const [openCalendar, setOpenCalendar] = useState(false);
  const [focusedInput, setFocusedInput] = useState<"startDate" | "endDate">(
    tripCategory === TripCategory.ONE_WAY
      ? "startDate"
      : departureDate
        ? returnDate
          ? "startDate"
          : "endDate"
        : "startDate"
  );

  // Adapter logic for new Calendar component
  const pickerType = useMemo(() => {
    return tripCategory === TripCategory.ONE_WAY
      ? PickerType.DAY
      : PickerType.RANGE;
  }, [tripCategory]);

  const handleDatesChangeNewCalendar = useCallback(
    (data: {
      startDate: Date | null;
      endDate: Date | null;
      focusedInput: "startDate" | "endDate" | null;
    }) => {
      switch (data.focusedInput) {
        case "startDate":
          setDepartureDate(null);
          setReturnDate(null);
          setFocusedInput(data.focusedInput);
          break;
        case "endDate":
          setDepartureDate(data.startDate);
          setReturnDate(null);
          setFocusedInput(data.focusedInput);
          break;
        case null:
          setDepartureDate(data.startDate);
          setReturnDate(data.endDate);
          setFocusedInput("startDate");
          break;
      }
    },
    [setDepartureDate, setReturnDate]
  );

  const handleCalendarClick = useCallback(() => {
    onCalendarClick?.();
    setOpenCalendar(!openCalendar);
  }, [onCalendarClick, openCalendar]);

  const closeCalendar = useCallback(() => {
    setOpenCalendar(false);
  }, []);

  const handleComplete = useCallback(() => {
    onComplete?.();
    closeCalendar();
  }, [closeCalendar, onComplete]);

  return (
    <DatePickerButton
      classes={[styles.button, ...classes]}
      startDate={departureDate}
      startLabel={startLabel || t?.("departure")}
      endDate={returnDate}
      endLabel={endLabel || t?.("return")}
      disabled={disabled}
      onClick={handleCalendarClick}
      dateFormat={dateFormat ?? "MMM D"}
      hideEndDate={tripCategory === TripCategory.ONE_WAY}
      renderCalendarPopup={(calendarProps) =>
        useNewCalendar ? (
          <HopperThemingProvider enableHDSTheme>
            <Calendar
              {...calendarProps}
              open={openCalendar}
              onClose={closeCalendar}
              onChooseDates={handleComplete}
              startDate={departureDate}
              endDate={returnDate}
              focusedInput={focusedInput}
              onDatesChange={handleDatesChangeNewCalendar}
              pickerType={pickerType}
              forceModal={true}
              departureDateBuckets={departureDateBucketsForNewCalendar}
              forceDisplayBucketsAndDeals={forceDisplayBucketsAndDeals}
              deals={dealsNewCalendar}
            />
          </HopperThemingProvider>
        ) : (
          <CalendarPicker
            {...calendarProps}
            prices={prices}
            paperClassName={paperClassName}
            onComplete={handleComplete}
            open={openCalendar}
            departureDate={departureDate}
            returnDate={returnDate}
            setDepartureDate={setDepartureDate}
            setReturnDate={setReturnDate}
            tripCategory={tripCategory}
            closePopup={closeCalendar}
            disabled={disabled}
            trackingProperties={trackingProperties}
            isMobile={matchesMobile}
            months={months}
            headerTitle={headerTitle}
            startDateLabel={startLabel}
            endDateLabel={endLabel}
            preventSameDateSelection={preventSameDateSelection}
          />
        )
      }
    />
  );
};
